import type { Cue, CueSeg } from '../model/cue.ts';

export const DEFAULT_ASR_LAST_ITEM_DURATION_MS = 500;

type Json3Event = {
  tStartMs?: number;
  dDurationMs?: number;
  segs?: Array<{ utf8?: string; tOffsetMs?: number }>;
};

type Json3Document = {
  events?: Json3Event[];
};

/** Mirrors legacy functions/youtube.js parseNormalSub. */
export function parseJson3Normal(json: Json3Document): Cue[] {
  const events = json.events ?? [];
  const subs: Cue[] = [];

  for (let i = 0; i < events.length; i += 1) {
    const segs = events[i]?.segs;
    if (!segs) continue;

    const childSegs: CueSeg[] = [];
    for (let j = 0; j < segs.length; j += 1) {
      const item = segs[j];
      if (!item) continue;
      const text = (item.utf8 ?? '').trim();
      if (text === '') continue;
      childSegs.push({
        start: events[i]?.tStartMs ?? 0,
        end: (events[i]?.tStartMs ?? 0) + (events[i]?.dDurationMs ?? 0),
        text,
      });
    }

    if (childSegs.length === 0) continue;

    const start = events[i]?.tStartMs ?? 0;
    const end = start + (events[i]?.dDurationMs ?? 0);
    const text = childSegs.map((seg) => seg.text).join(' ').trim();
    subs.push({ start, end, text, segs: childSegs });
  }

  return subs;
}

/** Mirrors legacy functions/youtube.js parseAsrSub. */
export function parseJson3Asr(
  json: Json3Document,
  asrLastItemDurationMs = DEFAULT_ASR_LAST_ITEM_DURATION_MS,
): Cue[] {
  const events = json.events ?? [];
  const subs: Cue[] = [];

  for (let i = 0; i < events.length; i += 1) {
    const segs = events[i]?.segs;
    if (!segs) continue;

    const lastItem = segs[segs.length - 1];
    const lastItemTOffsetMs = lastItem?.tOffsetMs ?? 0;
    const childSegs: CueSeg[] = [];

    for (let j = 0; j < segs.length; j += 1) {
      const item = segs[j];
      if (!item) continue;
      const itemOffsetMs = item.tOffsetMs ?? 0;
      const segStart = (events[i]?.tStartMs ?? 0) + itemOffsetMs;
      let segEnd: number;

      if (j !== segs.length - 1) {
        const nextOffsetMs = segs[j + 1]?.tOffsetMs ?? 0;
        segEnd = (events[i]?.tStartMs ?? 0) + nextOffsetMs - 1;
      } else if (i !== events.length - 1) {
        const nextStart = events[i + 1]?.tStartMs ?? 0;
        if ((events[i]?.tStartMs ?? 0) + itemOffsetMs + asrLastItemDurationMs < nextStart) {
          segEnd = (events[i]?.tStartMs ?? 0) + itemOffsetMs + asrLastItemDurationMs;
        } else {
          segEnd = nextStart - 1;
        }
      } else {
        segEnd = (events[i]?.tStartMs ?? 0) + itemOffsetMs + asrLastItemDurationMs;
      }

      const text = (item.utf8 ?? '').trim();
      if (text === '') continue;
      childSegs.push({ start: segStart, end: segEnd, text });
    }

    if (childSegs.length === 0) continue;

    const start = events[i]?.tStartMs ?? 0;
    let end: number;
    if (
      i !== events.length - 1
      && (events[i + 1]?.tStartMs ?? 0)
        < (events[i]?.tStartMs ?? 0) + lastItemTOffsetMs + asrLastItemDurationMs
    ) {
      end = events[i + 1]?.tStartMs ?? 0;
    } else {
      end = (events[i]?.tStartMs ?? 0) + lastItemTOffsetMs + asrLastItemDurationMs;
    }

    const text = childSegs.map((seg) => seg.text).join(' ').trim();
    subs.push({ start, end, text, segs: childSegs });
  }

  return subs;
}
